import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'diu', label: 'What DIUs Actually Are', level: 2 },
  { id: 'copy-vs-dataflow', label: 'Copy Activity vs Data Flow', level: 2 },
  { id: 'self-hosted-ir', label: 'Self-hosted IR: The Hidden Hourly Charge', level: 2 },
  { id: 'orchestration', label: 'Pipeline Orchestration: Free Tier and Beyond', level: 2 },
  { id: 'cost-management', label: 'Finding Your Actual ADF Costs', level: 2 },
  { id: 'calculator', label: 'ADF Cost Calculator', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="DP-203: Data Engineering on Microsoft Azure"
    url="https://www.pluralsight.com/paths/microsoft-azure-data-engineering-dp-203"
    price="From £29/month"
    badgeText="Covers ADF, Synapse, Data Lake"
  />
);

export default function AzureDataFactoryPricingGuide() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Data Factory Pricing UK 2026: DIUs, Data Flow and Hidden Costs',
            description:
              'Why ADF billing is deliberately opaque, what DIUs actually cost, Copy Activity vs Data Flow pricing, and how to find your real ADF spend in Azure Cost Management.',
            author: { '@type': 'Person', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-04-09',
            url: 'https://www.azure-calc.co.uk/guides/azure-data-factory-pricing-uk',
          }),
        }}
      />
      <GuideLayout
        title="Azure Data Factory Pricing UK 2026: DIUs, Data Flow and Hidden Costs"
        description="Microsoft's own engineering blog admitted ADF charges 'lack the clarity and transparency that we thrive to provide.' Here's what ADF actually costs in UK South — and where the surprises hide."
        lastUpdated="April 2026"
        canonical="https://www.azure-calc.co.uk/guides/azure-data-factory-pricing-uk"
        metaTitle="Azure Data Factory Pricing UK 2026 | AzureCalc.uk"
        metaDescription="Azure Data Factory pricing explained for UK South in GBP, including DIUs, Data Flow vCore costs, Self-hosted IR charges, and orchestration."
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'data-factory', path: '/data-factory', label: 'Data Factory Calculator' },
          { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
        ]}
        bottomCta={bottomCta}
      >

        {/* ── Introduction ─────────────────────────────────────────────── */}
        <p>
          Azure Data Factory's billing is deliberately opaque. Microsoft's own engineering blog
          admitted the charges <em>"lack the clarity and transparency that we thrive to provide."</em>{' '}
          The core confusion is the DIU — the Data Integration Unit — a compute abstraction that
          scales with data volume and parallelism in ways that aren't immediately obvious from the
          Azure portal.
        </p>

        <p>
          The "Auto" DIU setting defaults to 4 DIUs minimum. This means that even a simple copy of
          1 MB of data incurs the same minimum cost as copying 4× as much data. Engineers
          deploying dev/test pipelines with the default settings can easily accumulate charges they
          didn't expect.
        </p>

        {/* ── Section 1: DIUs ──────────────────────────────────────────── */}
        <h2 id="diu">What DIUs Actually Are</h2>

        <p>
          A Data Integration Unit (DIU) is a combination of CPU, memory, and network resources
          allocated to your Copy Activity. It's not a fixed size — it's a unit of parallelism and
          compute that Azure assigns based on the DIU count you specify.
        </p>

        <h3>The "Auto" default is 4 DIUs minimum</h3>

        <p>
          When you set DIU count to "Auto" (the default), Azure selects between 2 and 256 DIUs
          based on data size and partitioning. The key detail: <strong>the minimum is always 4 DIUs</strong>,
          regardless of how small the data transfer is. You cannot use 1 or 2 DIUs with Auto mode.
        </p>

        <p>
          At UK South rates, Copy Activity costs approximately <strong>£0.1881/DIU-hour</strong>
          (sourced from the Azure Retail Prices API). This means:
        </p>

        <ul>
          <li>A 10-minute pipeline run at 4 DIUs = 4 × (10/60) × £0.1881 = <strong>£0.125 per run</strong></li>
          <li>The same pipeline at 2 DIUs = 2 × (10/60) × £0.1881 = <strong>£0.063 per run</strong></li>
          <li>At 100 runs/month: 4 DIUs costs £12.54/month, 2 DIUs costs £6.27/month</li>
        </ul>

        <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#107c10]">lightbulb</span>
          <p className="text-sm text-on-surface">
            <strong className="text-[#107c10]">Practical tip:</strong> For dev/test pipelines
            that move small amounts of data, manually set the DIU count to 2. This halves your
            Copy Activity cost. Production pipelines processing large datasets benefit from higher
            DIU counts for parallel throughput, so leave those on Auto.
          </p>
        </div>

        <h3>How DIU-hours are calculated</h3>
        <p>
          DIU-hours = number of runs × (duration in hours) × DIU count. This is billed at the
          end of the month. If your pipeline runs 500 times per month, for 5 minutes each, at 4 DIUs:
        </p>
        <p>
          500 × (5/60) × 4 × £0.1881 = <strong>£31.35/month</strong>
        </p>

        {/* ── Section 2: Copy vs Data Flow ─────────────────────────────── */}
        <h2 id="copy-vs-dataflow">Copy Activity vs Data Flow: When to Use Each</h2>

        <h3>Copy Activity</h3>
        <p>
          Copy Activity is optimised for data movement — extracting from a source, optionally
          transforming lightly, and loading to a destination. It uses DIU-hours and is the
          cheapest option for straightforward ELT workloads.
        </p>

        <h3>Data Flow</h3>
        <p>
          Data Flow runs Spark-based transformations on a managed cluster. It's appropriate when
          you need complex transformations (joins, aggregations, pivots) that would be awkward in
          Copy Activity mapping. The cost model is fundamentally different:
        </p>

        <ul>
          <li>
            <strong>General Purpose cluster:</strong> approximately £0.216/vCore-hour
          </li>
          <li>
            <strong>Memory Optimised cluster:</strong> approximately £0.270/vCore-hour
          </li>
          <li>
            <strong>Compute Optimised cluster:</strong> approximately £0.172/vCore-hour
          </li>
        </ul>

        <p>
          The minimum cluster size is 8 vCores. A General Purpose cluster running for 10 hours/month:
          8 × 10 × £0.216 = <strong>£17.28/month</strong>. This is the minimum possible Data Flow
          charge if you run any jobs.
        </p>

        <h3>The cluster startup trap</h3>
        <p>
          Data Flow clusters take 1–4 minutes to start. Every time a pipeline triggers a Data Flow
          activity after the cluster has shut down, you pay for that startup time. The Time-To-Live
          (TTL) setting keeps the cluster warm between activities — typically worth setting to 10
          minutes if you trigger Data Flows more than once per hour.
        </p>

        {/* ── Section 3: Self-hosted IR ────────────────────────────────── */}
        <h2 id="self-hosted-ir">Self-hosted IR: The Hidden Hourly Charge</h2>

        <p>
          A Self-hosted Integration Runtime (IR) runs on your own infrastructure — typically a
          Windows VM — and enables ADF to connect to on-premises data sources or private networks.
          It's charged at approximately <strong>£0.0752/hour</strong> per license, separate from
          the VM compute costs you're already paying.
        </p>

        <p>
          This is billed as long as the IR is registered and active — not just when it's processing
          data. An always-on Self-hosted IR costs approximately <strong>£55.9/month</strong> (£0.0752 × 744)
          in licensing fees, on top of whatever VM you're running it on.
        </p>

        <div className="bg-[#7a4f00]/10 border border-[#7a4f00]/40 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#d08700]" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
          <p className="text-sm text-[#c09040]">
            <strong className="text-[#d08700]">Check your Self-hosted IR count.</strong> If you
            have multiple Self-hosted IR registrations in Azure Data Factory, each one incurs the
            hourly charge independently. Engineers who set up test IRs and forgot about them are
            often surprised to see this on the bill.
          </p>
        </div>

        {/* ── Section 4: Orchestration ─────────────────────────────────── */}
        <h2 id="orchestration">Pipeline Orchestration: Free Tier and Beyond</h2>

        <p>
          Azure Data Factory includes <strong>1,000 free pipeline activity runs per month</strong>
          for Azure-hosted Integration Runtime. This covers trigger runs, pipeline runs, and
          individual activity executions within a pipeline.
        </p>

        <p>
          Above the free tier, activity runs are charged per 1,000 runs. At UK South rates:
        </p>

        <ul>
          <li>0–1,000 runs/month: <strong>Free</strong></li>
          <li>1,001–100,000 runs/month: approximately <strong>£0.45 per 1,000 runs</strong></li>
          <li>100,000+ runs/month: approximately <strong>£0.36 per 1,000 runs</strong></li>
        </ul>

        <p>
          A pipeline that triggers 100 activities per run and runs 50 times per day generates
          150,000 activity executions per month — 149,000 of which are paid. At the tiered rates,
          that's approximately £63/month in orchestration costs.
        </p>

        {/* ── Section 5: Cost Management ───────────────────────────────── */}
        <h2 id="cost-management">Finding Your Actual ADF Costs in Azure Cost Management</h2>

        <p>
          In Azure Cost Management, filter by:
        </p>
        <ul>
          <li><strong>Service name:</strong> Azure Data Factory v2</li>
          <li><strong>Meter:</strong> Select individual meters (Cloud Data Movement, On Premises Data Movement, etc.)</li>
        </ul>

        <p>
          The breakdown you'll see:
        </p>
        <ul>
          <li><em>Cloud Data Movement</em> — Copy Activity DIU-hours (Azure-hosted IR)</li>
          <li><em>On Premises Data Movement</em> — Self-hosted IR hourly licensing</li>
          <li><em>Cloud Low Frequency Activity</em> — Pipeline activity run charges</li>
          <li><em>Cloud High Frequency Activity</em> — Trigger run charges</li>
        </ul>

        <p>
          Note that Data Flow charges appear under a separate resource within the same ADF instance.
          If you're using Data Flow, look for <em>vCore Hours</em> meters in the Cost Management breakdown.
        </p>

        {/* ── Calculator embed ─────────────────────────────────────────── */}
        <h2 id="calculator">Estimate Your Data Factory Costs</h2>

        <CalculatorEmbed service="data-factory" />

        <p>
          <em>
            Built and verified by an independent Azure engineer.{' '}
            <a href="/methodology" className="text-primary hover:underline">
              See methodology →
            </a>
          </em>
        </p>

      </GuideLayout>
    </>
  );
}
